import {
  Box,
  Divider,
  Flex,
  Modal,
  ModalContent,
  ModalOverlay,
  Text,
  useBreakpointValue,
  useToken,
} from "@chakra-ui/react";
import { FormattedMessage, useIntl } from "react-intl";

import { Creator, PublicationReadDetail } from "@nens/rana/api-v1";
import { ButtonWrapper, RanaHeading, SIZES } from "@nens/ranalib";
import { RanaForm } from "@nens/ranalib";
import { hexToRgb } from "@nens/shared-utils";

import { Profile } from "@/lib/profile";

import { PublicationPublicGoToNormalViewerButton } from "./public-go-to-normal-viewer-button";

type PublicationPublicAccountModalProps = {
  publication: PublicationReadDetail;
  publicationId: string;
  setPublicUser: (publicUser: Creator) => void;
  initialUser?: { givenName: string; familyName: string; email: string };
  onClose?: () => void;
};

const VALIDATION_EMAIL = /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/;
const MODAL_BORDER_WIDTH = 11;

/**
 * Pure modal UI. Renders the "enter your name" form for an anonymous user
 * accessing a shared publication. Requires the publication to already be fetched.
 */
export function PublicationPublicAccountModal({
  publication,
  publicationId,
  setPublicUser,
  initialUser = { givenName: "", familyName: "", email: "" },
  onClose,
}: PublicationPublicAccountModalProps) {
  const intl = useIntl();

  const isHorizontalLayout = useBreakpointValue<boolean>({
    base: true,
    sm: false,
    md: true,
  });

  const modalBackdropColor = useToken("colors", "brand.modal_backdrop");

  const { creator, name, project } = publication;

  const projectSlug = project.slug;

  return (
    <RanaForm
      validate={(values) => {
        const errors: Record<string, string> = {};

        if (values.email && !VALIDATION_EMAIL.test(values.email)) {
          errors.email = intl.formatMessage({
            id: "error.validation.email",
            defaultMessage: "The email is invalid.",
          });
        }

        return errors;
      }}
      initialValues={initialUser}
      doSubmit={(values) => {
        setPublicUser(values);
        if (onClose) onClose();
      }}
    >
      <Modal isOpen isCentered onClose={onClose ?? (() => undefined)}>
        <ModalOverlay
          backdropFilter="blur(5px)"
          bg={`rgba(${hexToRgb(modalBackdropColor)}, 0.8)`}
        />
        <ModalContent
          minW={{ lg: "lg" }}
          w="fit"
          maxW={`calc(100% - ${SIZES[11]})`}
          h="fit"
          maxH={`calc(100% - ${SIZES[11]})`}
          borderRadius="base"
          bg="brand.grey.100"
        >
          <Flex justifyContent="center" direction={{ base: "column", md: "row" }}>
            <Flex
              flexDirection={{ base: "column", sm: "row", md: "column" }}
              flex="1"
              bg="brand.light_sky"
              p={8}
              borderTopLeftRadius="base"
              borderBottomLeftRadius="base"
              alignItems="center"
              justifyContent="center"
            >
              <Flex
                alignItems={{ sm: "center", md: "flex-start" }}
                flexDirection={{ base: "column", sm: "row", md: "column" }}
                w={{ md: "fit" }}
              >
                <Flex
                  flexDirection={{ base: "column", sm: "row", md: "column" }}
                  flexWrap="wrap"
                  gap={6}
                  justifyContent="center"
                  alignItems="center"
                  minW="1/2"
                  minH={{ md: "50%" }}
                  w="fit"
                  pb={isHorizontalLayout ? 8 : undefined}
                  pr={!isHorizontalLayout ? 4 : undefined}
                >
                  <Profile user={creator} boxSize={24} />
                  <Flex gap={2} direction="column">
                    <Box>
                      <RanaHeading maxW={56} type="h4">
                        {creator.givenName} {creator.familyName}
                      </RanaHeading>
                    </Box>

                    <Text>
                      <FormattedMessage
                        id="publication.publication.creator_shared"
                        defaultMessage="Shared a publication"
                      />
                    </Text>
                  </Flex>
                </Flex>

                <Divider
                  orientation={isHorizontalLayout ? "horizontal" : "vertical"}
                  color="brand.grey.100"
                  borderWidth="base"
                  h={!isHorizontalLayout ? 24 : undefined}
                  mb={isHorizontalLayout ? 8 : undefined}
                  mr={!isHorizontalLayout ? 8 : undefined}
                />
                <Box minW="fit" w="fit" overflow="hidden">
                  <Text textTransform="uppercase" mb={2}>
                    <FormattedMessage
                      id="publication.publication_shared"
                      defaultMessage="Publication"
                    />
                  </Text>
                  <Text whiteSpace="pre-wrap" fontWeight="bold" textTransform="capitalize">
                    {name}
                  </Text>
                </Box>
              </Flex>
            </Flex>
            <Box p={MODAL_BORDER_WIDTH} maxW="3xl" flex="3" bg="brand.grey.100">
              <RanaHeading type="h3" mb={4}>
                <FormattedMessage
                  id="publication.public_greeting"
                  defaultMessage="Nice to meet you!"
                />
              </RanaHeading>
              <Text my={6}>
                <FormattedMessage
                  id="publication.public_introduction"
                  defaultMessage="Hi! We're excited you want to explore this publication. The creator, {name}, would love to know who's viewing it. Could you please leave your name and email before you continue? Thanks a lot!"
                  values={{
                    name: (
                      <strong>
                        {creator.givenName} {creator.familyName}
                      </strong>
                    ),
                  }}
                />
              </Text>
              <Flex w="full" flexDirection={{ base: "column", md: "row" }} gap={4}>
                <RanaForm.Input
                  flex={1}
                  field="givenName"
                  label={intl.formatMessage({
                    id: "form.label.first_name",
                    defaultMessage: "First name",
                  })}
                  explanation={intl.formatMessage({
                    id: "form.explanation.enter_first_name",
                    defaultMessage: "Your first name",
                  })}
                  isRequired
                />
                <RanaForm.Input
                  flex={1}
                  field="familyName"
                  label={intl.formatMessage({
                    id: "form.label.last_name",
                    defaultMessage: "Last name",
                  })}
                  explanation={intl.formatMessage({
                    id: "form.explanation.enter_last_name",
                    defaultMessage: "Your last name",
                  })}
                  isRequired
                />
              </Flex>
              <RanaForm.Input
                field="email"
                type="email"
                label={intl.formatMessage({
                  id: "form.label.email",
                  defaultMessage: "Email address",
                })}
                explanation={intl.formatMessage({
                  id: "form.explanation.enter_email",
                  defaultMessage: "name@example.com",
                })}
                isRequired
              />
              <RanaForm.Errors />
              <ButtonWrapper alignItems="center">
                <RanaForm.SubmitButton my={4}>
                  <FormattedMessage
                    id="publication.open_publication_as_guest"
                    defaultMessage="Open publication as guest"
                  />
                </RanaForm.SubmitButton>
                <PublicationPublicGoToNormalViewerButton
                  projectSlug={projectSlug}
                  publicationId={publicationId}
                />
              </ButtonWrapper>
            </Box>
          </Flex>
        </ModalContent>
      </Modal>
    </RanaForm>
  );
}
